import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { cn } from '@zep/utils/tailwind';
import { getLandingImageUrl } from '../../../../consts/landingImages';
import { useLocale } from '../../../../hooks';
import { ProductDescription, ProductTheme } from './types';
interface DesktopTabViewProps {
  descriptions: readonly ProductDescription[];
  theme: ProductTheme;
  activeIndex?: number;
  onTabClick?: (index: number) => void;
}
export const DesktopTabView = ({
  descriptions,
  theme,
  activeIndex: controlledActiveIndex,
  onTabClick
}: DesktopTabViewProps) => {
  const locale = useLocale();
  const [internalActiveIndex, setInternalActiveIndex] = useState(0);
  const activeIndex = controlledActiveIndex ?? internalActiveIndex;
  const handleTabClick = (index: number) => {
    if (onTabClick) {
      onTabClick(index);
    } else {
      setInternalActiveIndex(index);
    }
  };
  const activeImageUrl = getLandingImageUrl(descriptions[activeIndex].imageKey, locale);
  return <div className={cn('flex flex-row', 'max-w-[1280px]', 'items-start', 'rounded-quiz-xl', 'overflow-hidden', 'border-1 border-[#1C1B1F0A]')} style={{
    backgroundColor: theme.backgroundColor,
    boxShadow: '0 2px 20px 2px rgba(0, 0, 0, 0.04)'
  }} data-sentry-component="DesktopTabView" data-sentry-source-file="DesktopTabView.tsx">
      <TabList descriptions={descriptions} activeIndex={activeIndex} onTabClick={handleTabClick} data-sentry-element="TabList" data-sentry-source-file="DesktopTabView.tsx" />
      <PreviewImage imageUrl={activeImageUrl} theme={theme} data-sentry-element="PreviewImage" data-sentry-source-file="DesktopTabView.tsx" />
    </div>;
};
interface TabListProps {
  descriptions: readonly ProductDescription[];
  activeIndex: number;
  onTabClick: (index: number) => void;
}
const TabList = ({
  descriptions,
  activeIndex,
  onTabClick
}: TabListProps) => {
  return <div className="flex w-[560px] shrink-0 flex-col gap-quiz-sm p-quiz-2xl" data-sentry-component="TabList" data-sentry-source-file="DesktopTabView.tsx">
      {descriptions.map((description, index) => <TabItem key={description.id} description={description} isActive={index === activeIndex} onClick={() => onTabClick(index)} />)}
    </div>;
};
interface TabItemProps {
  description: ProductDescription;
  isActive: boolean;
  onClick: () => void;
}
const TabItem = ({
  description,
  isActive,
  onClick
}: TabItemProps) => {
  const {
    t
  } = useTranslation(['home']);
  const IconComponent = description.icon;
  return <motion.button type="button" layout onClick={onClick} transition={{
    duration: 0.2,
    ease: 'easeInOut'
  }} className={cn('flex flex-col', 'w-fit', 'px-quiz-lg py-quiz-md tablet:px-quiz-lg tablet:py-quiz-lg', 'rounded-quiz-xl', 'text-left', 'transition-colors duration-200 ease-in-out', 'cursor-pointer', isActive ? 'bg-gradient-to-r from-white/20 via-quiz-violet-50/20 to-white/20 bg-white/80' : 'bg-quiz-opacity-white-5 opacity-80 hover:opacity-100 hover:bg-white/40')} data-sentry-element="unknown" data-sentry-component="TabItem" data-sentry-source-file="DesktopTabView.tsx">
      <motion.div layout="position" className="flex items-center gap-quiz-2xs" data-sentry-element="unknown" data-sentry-source-file="DesktopTabView.tsx">
        {IconComponent && <IconComponent className="size-6 shrink-0 text-quiz-opacity-black-6" />}
        <h3 className="whitespace-nowrap text-quiz-heading-sm-bold text-quiz-gray-900">
          {t(description.title)}
        </h3>
      </motion.div>
      <AnimatePresence initial={false} data-sentry-element="AnimatePresence" data-sentry-source-file="DesktopTabView.tsx">
        {isActive && description.description && <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease: 'easeInOut'
      }} className="max-w-[480px] pl-quiz-2xl pt-quiz-md text-quiz-body-md-medium text-quiz-gray-700">
            {t(description.description)}
          </motion.p>}
      </AnimatePresence>
    </motion.button>;
};
interface PreviewImageProps {
  imageUrl: string;
  theme: ProductTheme;
}
const PreviewImage = ({
  imageUrl,
  theme
}: PreviewImageProps) => {
  return <div className="relative h-[644px] w-[720px] shrink-0 overflow-hidden" data-sentry-component="PreviewImage" data-sentry-source-file="DesktopTabView.tsx">
      <AnimatePresence mode="wait" data-sentry-element="AnimatePresence" data-sentry-source-file="DesktopTabView.tsx">
        <motion.div key={imageUrl} initial={{
        x: 100,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: -100,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeInOut'
      }} className="absolute inset-0" data-sentry-element="unknown" data-sentry-source-file="DesktopTabView.tsx">
          <Image src={imageUrl} alt="Product preview" fill className="object-cover object-center" priority={false} data-sentry-element="Image" data-sentry-source-file="DesktopTabView.tsx" />
        </motion.div>
      </AnimatePresence>
      {/* Left Gradient Fade */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-[90px] bg-gradient-to-r to-transparent" style={{
      backgroundImage: theme.gradientFrom ? `linear-gradient(to right, ${theme.gradientFrom}, transparent)` : 'linear-gradient(to right, rgb(237 233 254), transparent)'
    }} />
    </div>;
};